import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Field, Loading } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { money, computeTotals, SERVICE_LABELS } from "@/lib/format";

const BLANK_ITEM = { description: "", quantity: 1, unit_price: 0, category: "service" };
const PRESETS = [["standard", "Standard cleaning"], ["deep", "Deep cleaning"], ["move", "Move-in/move-out cleaning"]];

function ItemRow({ item, idx, onChange, onRemove, canRemove }) {
  const set = (k, v) => onChange(idx, { ...item, [k]: v });
  return (
    <div className="grid grid-cols-12 gap-2 items-start py-2 border-b border-slate-100" data-testid={`line-item-${idx}`}>
      <Input className="col-span-12 sm:col-span-6 h-11" placeholder="Description" value={item.description} onChange={(e) => set("description", e.target.value)} data-testid={`item-description-${idx}`} />
      <Input className="col-span-3 sm:col-span-2 h-11" type="number" min="0.01" step="0.01" value={item.quantity} onChange={(e) => set("quantity", e.target.value)} aria-label="Quantity" data-testid={`item-quantity-${idx}`} />
      <Input className="col-span-4 sm:col-span-2 h-11" type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => set("unit_price", e.target.value)} aria-label="Unit price" data-testid={`item-price-${idx}`} />
      <div className="col-span-3 sm:col-span-1 h-11 flex items-center justify-end text-sm font-medium text-slate-900">{money((Number(item.quantity) || 0) * (Number(item.unit_price) || 0))}</div>
      <Button type="button" variant="ghost" size="icon" className="col-span-2 sm:col-span-1 h-11 w-11" disabled={!canRemove} onClick={() => onRemove(idx)} aria-label="Remove item" data-testid={`item-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export default function DocEditor({ kind }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isEst = kind === "estimate";
  const base = isEst ? "/estimates" : "/invoices";
  const dateKey = isEst ? "valid_until" : "due_date";
  const [clients, setClients] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([api.get("/clients"), api.get("/business")]);
        setPricing(b.data.pricing);
        let list = c.data;
        if (id) {
          const { data: d } = await api.get(`${base}/${id}`);
          if (!list.find((x) => x.id === d.client_id)) list = [...list, { id: d.client_id, name: d.client_name }];
          setForm({ client_id: d.client_id, service_type: d.service_type || "standard", items: d.items.map(({ amount, ...i }) => i), discount_type: d.discount_type, discount_value: d.discount_value, tax_rate: d.tax_rate, notes: d.notes || "", date: d[dateKey] || "", appointment_id: d.appointment_id || null });
        } else {
          const st = params.get("service_type") || "standard";
          const price = params.get("price");
          setForm({ client_id: params.get("client_id") || "", service_type: st, items: price ? [{ ...BLANK_ITEM, description: SERVICE_LABELS[st], unit_price: Number(price) }] : [{ ...BLANK_ITEM }], discount_type: "amount", discount_value: 0, tax_rate: b.data.default_tax_rate || 0, notes: "", date: "", appointment_id: params.get("appointment_id") });
        }
        setClients(list);
      } catch (e) { setError(errMsg(e)); }
    })();
  }, [id, base, dateKey, params]);

  const totals = useMemo(() => form && computeTotals(form.items, form.discount_type, form.discount_value, form.tax_rate), [form]);
  if (!form) return error ? <p className="text-red-700">{error}</p> : <Loading />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, item) => set("items", form.items.map((x, i) => (i === idx ? item : x)));
  const addItem = (item) => set("items", [...form.items.filter((i) => i.description || Number(i.unit_price)), item]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) return setError("Please choose a client.");
    setBusy(true); setError("");
    const payload = {
      client_id: form.client_id, service_type: form.service_type, discount_type: form.discount_type, discount_value: Number(form.discount_value) || 0,
      tax_rate: Number(form.tax_rate) || 0, notes: form.notes, [dateKey]: form.date || null,
      items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
      ...(isEst ? {} : { appointment_id: form.appointment_id || null }),
    };
    try {
      const { data } = id ? await api.put(`${base}/${id}`, payload) : await api.post(base, payload);
      toast.success(`${isEst ? "Estimate" : "Invoice"} saved`);
      navigate(`${base}/${data.id}`);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid={`${kind}-editor`}>
      <Link to={id ? `${base}/${id}` : base} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="h-4 w-4" />Back</Link>
      <PageHeader title={`${id ? "Edit" : "New"} ${kind}`} />
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Panel>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Client *">
                <Select value={form.client_id} onValueChange={(v) => set("client_id", v)} disabled={!!id}>
                  <SelectTrigger className="h-11" data-testid="doc-client-select"><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Service type">
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger className="h-11" data-testid="doc-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SERVICE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={isEst ? "Valid until" : "Due date"} htmlFor="doc-date" hint={isEst ? "" : "Defaults to your payment terms"}><Input id="doc-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-11" data-testid="doc-date-input" /></Field>
            </div>
          </Panel>
          <Panel title="Line items" testId="line-items-panel">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-slate-500 pb-1">
              <span className="col-span-6">Description</span><span className="col-span-2">Qty</span><span className="col-span-2">Unit price</span><span className="col-span-1 text-right">Amount</span>
            </div>
            {form.items.map((it, i) => <ItemRow key={i} idx={i} item={it} onChange={setItem} onRemove={(x) => set("items", form.items.filter((_, j) => j !== x))} canRemove={form.items.length > 1} />)}
            <div className="flex flex-wrap gap-2 mt-4">
              <Button type="button" variant="outline" className="h-10" onClick={() => set("items", [...form.items, { ...BLANK_ITEM }])} data-testid="add-line-item-btn"><Plus className="h-4 w-4 mr-1" />Custom item</Button>
              {pricing && PRESETS.map(([k, l]) => <Button key={k} type="button" variant="secondary" className="h-10" onClick={() => addItem({ description: l, quantity: 1, unit_price: pricing.base_prices[k], category: "service" })} data-testid={`preset-${k}`}>+ {l}</Button>)}
              {pricing?.extras.map((x) => <Button key={x.key} type="button" variant="secondary" className="h-10" onClick={() => addItem({ description: x.name, quantity: 1, unit_price: x.price, category: "addon" })} data-testid={`preset-addon-${x.key}`}>+ {x.name}</Button>)}
            </div>
          </Panel>
          <Panel title="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Shown to the client" data-testid="doc-notes-input" /></Panel>
        </div>
        <div>
          <Panel title="Summary" className="lg:sticky lg:top-6" testId="doc-summary">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Discount">
                <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
                  <SelectTrigger className="h-11" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="amount">$ amount</SelectItem><SelectItem value="percent">% percent</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Value" htmlFor="disc"><Input id="disc" type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} className="h-11" data-testid="discount-value-input" /></Field>
              <Field label="Tax rate (%)" htmlFor="tax" className="col-span-2" hint="Only if sales tax applies to your services."><Input id="tax" type="number" min="0" max="30" step="0.01" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className="h-11" data-testid="tax-rate-input" /></Field>
            </div>
            <dl className="text-sm space-y-2 border-t border-slate-100 pt-4">
              <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid="editor-subtotal">{money(totals.subtotal)}</dd></div>
              <div className="flex justify-between"><dt>Discount</dt><dd data-testid="editor-discount">−{money(totals.discount)}</dd></div>
              <div className="flex justify-between"><dt>Tax</dt><dd data-testid="editor-tax">{money(totals.tax)}</dd></div>
              <div className="flex justify-between text-lg font-semibold text-slate-900 pt-2 border-t border-slate-100"><dt>Total</dt><dd data-testid="editor-total">{money(totals.total)}</dd></div>
            </dl>
            {error && <p className="text-sm text-red-700 mt-3" role="alert" data-testid="doc-editor-error">{error}</p>}
            <Button type="submit" className="w-full h-11 mt-4" disabled={busy} data-testid="save-doc-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save {kind}</Button>
          </Panel>
        </div>
      </div>
    </form>
  );
}
